import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'

interface EaCheck {
  status: 'exists' | 'canceled' | 'deleted' | 'pending' | 'unconfigured' | 'error'
  idorder?: string | null
  note?: string
}
const EA_CHECK_TEXT: Record<EaCheck['status'], { t: string; c: string }> = {
  exists: { t: 'Există în ExpertAccounts', c: 'border-green-200 bg-green-50 text-green-800' },
  canceled: { t: 'Anulată în ExpertAccounts', c: 'border-amber-200 bg-amber-50 text-amber-800' },
  deleted: {
    t: 'NU există în ExpertAccounts — ștearsă',
    c: 'border-red-200 bg-red-50 text-red-800',
  },
  pending: {
    t: 'Se pregătește lista comenzilor din ExpertAccounts. Apasă din nou „Verifică în EA” peste un minut.',
    c: 'border-blue-200 bg-blue-50 text-blue-800',
  },
  unconfigured: {
    t: 'Legătura cu ExpertAccounts pentru comenzi nu e configurată. Cere administratorului s-o completeze în Setări → Comenzi.',
    c: 'border-gray-200 bg-gray-50 text-gray-700',
  },
  error: { t: 'Eroare la verificare', c: 'border-red-200 bg-red-50 text-red-800' },
}

interface SpvDebug {
  order: {
    id: number
    code: string
    status: string
    totalWithVat: number
    eaDocId: string | null
    eaSentAt: string | null
    clientName: string | null
    clientCif: string | null
    cifDigits: string
  }
  primary: { inboundId: number; docId: string | null; downloaded: boolean }[]
  cifCandidates: {
    inboundId: number
    docId: string | null
    orderCode: string | null
    totalWithTax: number | null
    totalDiff: number | null
    issueDate: string | null
    downloaded: boolean
  }[]
  anaf: {
    enabled: boolean
    tokenExpiresAt: string | null
    tokenExpired: boolean
    lastListSyncAt: string | null
    lastListSyncCount: number | null
    sentTotal: number
    sentUndownloaded: number
    sentToday: number
  }
  reason: string
}

const dt = (v: string | null) => (v ? new Date(v).toLocaleString('ro-RO') : '—')

/** Diagnostic „de ce nu se leagă factura SPV" pentru o comandă (doar admin). */
export function SpvDebugModal({
  order,
  onClose,
}: {
  order: { id: number; code: string } | null
  onClose: () => void
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['spv-debug', order?.id],
    queryFn: async () => (await api.get<SpvDebug>(`/orders/${order!.id}/spv-debug`)).data,
    enabled: order != null,
  })

  const qc = useQueryClient()
  const [eaCheck, setEaCheck] = useState<EaCheck | null>(null)
  useEffect(() => setEaCheck(null), [order?.id])
  const errMsg = (e: unknown) =>
    (e as { response?: { data?: { error?: { message?: string } } }; message?: string }).response
      ?.data?.error?.message ?? (e as { message?: string }).message
  const eaCheckMut = useMutation({
    mutationFn: async () =>
      (await api.get<EaCheck>(`/orders/${order!.id}/ea-check`, { timeout: 120_000 })).data,
    onSuccess: (r) => setEaCheck(r),
    onError: (e: unknown) => setEaCheck({ status: 'error', note: errMsg(e) }),
  })
  const markCanceledMut = useMutation({
    mutationFn: async () => (await api.post(`/orders/${order!.id}/mark-canceled-ea`)).data,
    onSuccess: () => {
      toast.success('Comandă marcată anulată în CRM.')
      void qc.invalidateQueries({ queryKey: ['orders'] })
      onClose()
    },
    onError: (e: unknown) => toast.error(errMsg(e) ?? 'Nu am putut marca anulată.'),
  })

  return (
    <Modal
      open={order != null}
      onClose={onClose}
      title={order ? `Diagnostic factură SPV — ${order.code}` : 'Diagnostic factură SPV'}
      size="lg"
      footer={<Button onClick={onClose}>Închide</Button>}
    >
      {isLoading ? (
        <p className="text-sm text-gray-500">Se încarcă…</p>
      ) : error || !data ? (
        <p className="text-sm text-red-600">Nu am putut rula diagnosticul.</p>
      ) : (
        <div className="space-y-4 text-sm">
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-amber-900">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
              Concluzie
            </div>
            {data.reason}
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={() => eaCheckMut.mutate()}
              loading={eaCheckMut.isPending}
              title="Caută comanda în ExpertAccounts după cod (refno)"
            >
              {eaCheckMut.isPending ? 'Se verifică…' : 'Verifică în EA'}
            </Button>
            {eaCheck && (
              <span
                className={`rounded border px-2 py-1 text-xs font-medium ${EA_CHECK_TEXT[eaCheck.status].c}`}
              >
                {EA_CHECK_TEXT[eaCheck.status].t}
                {eaCheck.idorder ? ` (id ${eaCheck.idorder})` : ''}
                {eaCheck.status === 'error' && eaCheck.note ? `: ${eaCheck.note}` : ''}
              </span>
            )}
          </div>

          {eaCheck?.status === 'deleted' && (
            <div className="flex items-center gap-3">
              <Button
                variant="danger"
                onClick={() => markCanceledMut.mutate()}
                loading={markCanceledMut.isPending}
                title="Trece comanda în Anulată în CRM (documentul e șters din EA)"
              >
                Marchează anulată în CRM
              </Button>
              <span className="text-xs text-gray-500">
                Comanda trece din „Trimisă în EA” în „Anulată” (istoricul se păstrează).
              </span>
            </div>
          )}

          <div className="grid gap-1 rounded border border-gray-200 p-3 text-xs">
            <div className="mb-1 font-semibold text-gray-700">Comandă</div>
            <div>
              Client: <b>{data.order.clientName ?? '—'}</b> · CIF {data.order.clientCif ?? '—'} (
              {data.order.cifDigits || 'lipsă'})
            </div>
            <div>
              Total: <b>{data.order.totalWithVat.toFixed(2)} lei</b> · Doc EA:{' '}
              {data.order.eaDocId ?? '—'} · Trimisă în EA: {dt(data.order.eaSentAt)}
            </div>
          </div>

          <div
            className={`rounded border p-3 text-xs ${
              !data.anaf.enabled || data.anaf.tokenExpired
                ? 'border-red-200 bg-red-50 text-red-800'
                : 'border-gray-200 text-gray-700'
            }`}
          >
            <div className="mb-1 font-semibold">Sincronizare ANAF (SPV)</div>
            <div>
              Activ: {data.anaf.enabled ? 'da' : 'NU'} · Token expiră:{' '}
              {dt(data.anaf.tokenExpiresAt)} {data.anaf.tokenExpired && <b>(EXPIRAT)</b>}
            </div>
            <div>
              Ultima sincronizare a listei: {dt(data.anaf.lastListSyncAt)} (
              {data.anaf.lastListSyncCount ?? 0} mesaje)
            </div>
            <div>
              Facturi TRIMISE în CRM: {data.anaf.sentTotal} · fără XML: {data.anaf.sentUndownloaded}{' '}
              · azi: {data.anaf.sentToday}
            </div>
          </div>

          <div className="text-xs">
            <div className="mb-1 font-semibold text-gray-700">
              Match primar (cod în XML): {data.primary.length > 0 ? '✓ găsit' : '✗ negăsit'}
            </div>
            {data.primary.map((p) => (
              <div key={p.inboundId} className="text-gray-600">
                factură #{p.docId ?? p.inboundId} · XML descărcat: {p.downloaded ? 'da' : 'nu'}
              </div>
            ))}
          </div>

          <div className="text-xs">
            <div className="mb-1 font-semibold text-gray-700">
              Facturi TRIMISE cu CIF-ul clientului ({data.cifCandidates.length})
            </div>
            {data.cifCandidates.length === 0 ? (
              <div className="text-gray-500">niciuna — factura nu e (încă) sincronizată</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="pr-3">Factură</th>
                      <th className="pr-3">Comandă</th>
                      <th className="pr-3">Total</th>
                      <th className="pr-3">Δ vs comandă</th>
                      <th className="pr-3">Emisă</th>
                      <th className="pr-3">XML</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cifCandidates.map((c) => (
                      <tr key={c.inboundId} className="border-t border-gray-100">
                        <td className="pr-3">{c.docId ?? c.inboundId}</td>
                        <td className="pr-3 font-mono text-[11px]">{c.orderCode ?? '—'}</td>
                        <td className="pr-3">{c.totalWithTax?.toFixed(2) ?? '—'}</td>
                        <td
                          className={`pr-3 ${
                            c.totalDiff != null && Math.abs(c.totalDiff) <= 1
                              ? 'text-green-700'
                              : 'text-gray-600'
                          }`}
                        >
                          {c.totalDiff != null ? `${c.totalDiff} lei` : '—'}
                        </td>
                        <td className="pr-3">{c.issueDate ? dt(c.issueDate) : '—'}</td>
                        <td className="pr-3">{c.downloaded ? 'da' : 'NU'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}
